/**
 * Cliente HTTP do admin-backend. Wrapper fino sobre fetch com tratamento
 * de erro consistente e credentials incluídas (pra cookie de sessão).
 */
const API_BASE = "/api";

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let parsed: any = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!res.ok) {
    const msg = parsed && typeof parsed === "object" && parsed.error ? parsed.error : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

export interface SocioMe {
  id: string;
  username: string;
  email: string;
  nomeCompleto: string;
  cargo: string;
  role: "SOCIO" | "ADMIN";
  smartAccount: string;
}

export interface AuthOutcome {
  socioId: string;
  username: string;
  nomeCompleto: string;
  role: "SOCIO" | "ADMIN";
  smartAccount: string;
}

export interface VestingStatus {
  hasVesting: boolean;
  vesting?: string;
  start?: number;
  duration?: number;
  cliffDuration?: number;
  released?: string;
  releasable?: string;
  total?: string;   // alocação total (no contrato + já sacado)
  locked?: string;  // ainda bloqueado (no contrato, não liberado)
  revoked?: boolean;
}

export interface TokenBalance {
  balance: string;
  votingPower: string;
  project?: {
    slug: string;
    tokenName: string;
    tokenSymbol: string;
    tokenAddress: string;
  };
}

export interface TokenHolding {
  slug: string;
  tokenName: string;
  tokenSymbol: string;
  tokenAddress: string;
  decimals: number;
  balance: string;
  votingPower: string;
}

export interface TokenBalances {
  address: string;
  tokens: TokenHolding[];
}

export const authApi = {
  login: (username: string, password: string) =>
    api.post<AuthOutcome>("/auth/login", { username, password }),
  activate: (username: string, password: string, smartAccount: string, code?: string) =>
    api.post<AuthOutcome>("/auth/activate", { username, password, smartAccount, code }),
  activateByCode: (code: string, password: string, smartAccount?: string, username?: string) =>
    api.post<AuthOutcome>("/auth/activate-code", { code, password, smartAccount, username }),
  logout: () => api.post<{ ok: boolean }>("/auth/logout"),
};

export interface Organization {
  id: string;
  nome: string;
  slug: string;
  role: "OWNER" | "MEMBER";
}

export interface OrgMember {
  socioId: string;
  nome: string;
  email: string;
  smartAccount: string;
  role: "OWNER" | "MEMBER";
  activated: boolean;
}

export interface InviteResult {
  status: "invited" | "added";
  email: string;
  nome: string;
  code: string | null;
}

export const orgsApi = {
  mine: () => api.get<{ organizations: Organization[] }>("/orgs"),
  create: (nome: string) => api.post<Organization>("/orgs", { nome }),
  members: (orgId: string) => api.get<{ members: OrgMember[] }>(`/orgs/${orgId}/members`),
  invite: (orgId: string, email: string, nome: string) =>
    api.post<InviteResult>(`/orgs/${orgId}/invite`, { email, nome }),
};

export interface ClaimResult {
  txHash: string;
  releasedNow: string;
  totalReleased: string;
  releasable: string;
}

export interface KeyVaultBlob {
  ciphertext: string;
  salt: string;
  iv: string;
  address: string;
}

export interface OnchainEvent {
  id: number;
  kind: string;
  description: string;
  txHash: string;
  address: string | null;
  fromAddr: string | null;
  toAddr: string | null;
  amountWei: string | null;
  blockNumber: string | null;
  slug: string | null;
  createdAt: string;
}

export const socioApi = {
  me: () => api.get<SocioMe>("/socio/me"),
  vesting: () => api.get<VestingStatus>("/socio/vesting"),
  balance: () => api.get<TokenBalance>("/socio/balance"),
  balances: () => api.get<TokenBalances>("/socio/balances"),
  claim: () => api.post<ClaimResult>("/socio/claim"),
  activity: () => api.get<{ events: OnchainEvent[] }>("/socio/activity"),
  // Key vault: backup da chave cifrada (servidor guarda blob opaco).
  getKeyVault: async (): Promise<KeyVaultBlob | null> => {
    try {
      return await api.get<KeyVaultBlob>("/socio/keyvault");
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  putKeyVault: (blob: KeyVaultBlob) => api.put<{ ok: boolean }>("/socio/keyvault", blob),
  deleteKeyVault: () => api.delete<{ ok: boolean }>("/socio/keyvault"),
};

// ─── Governança ──────────────────────────────────────────────────

export interface ProposalOption {
  label: string;
  votes: string;
}

export interface Proposal {
  proposalId: string;
  description: string | null;
  proposer: string | null;
  createdAt: string;
  state: string;
  options: ProposalOption[];   // opção por índice (0..N-1) com rótulo + votos
  abstainVotes: string;
  totalVotes: string;
  deadline: string | null;
}

export interface VotingMeta {
  governor: string;
  token: string;
  governorNonce: string;
  governorName: string;
  tokenNonce: string;
  tokenName: string;
  votingPower: string;
  delegatee: string;
}

export const governanceApi = {
  list: () => api.get<{ proposals: Proposal[] }>("/socio/proposals"),
  create: (description: string, options?: string[]) =>
    api.post<{ proposalId: string; txHash: string }>("/socio/proposals", { description, options }),
  votingMeta: () => api.get<VotingMeta>("/socio/voting-meta"),
  vote: (proposalId: string, support: number, signature: string) =>
    api.post<{ txHash: string }>(`/socio/proposals/${proposalId}/vote`, { support, signature }),
  delegate: (nonce: string, expiry: string, v: number, r: string, s: string) =>
    api.post<{ txHash: string }>("/socio/delegate", { nonce, expiry, v, r, s }),
};

// ─── Deploy (admin-only) ─────────────────────────────────────────

export interface AvailableContracts {
  contracts: string[];
}

export interface Deployment {
  contractName: string;
  address: string;
  txHash: string;
  blockNumber: string;
  deployerAddress: string;
  deployedAt: string;
  label?: string;
}

export interface DeployRequest {
  contractName: string;
  constructorArgs: (string | number | boolean | string[])[];
  label?: string;
}

export type FieldKind =
  | "address" | "address-list" | "string" | "symbol"
  | "tokens" | "uint256"
  | "duration-months" | "duration-seconds" | "timestamp" | "bool";

export interface ContractField {
  name: string;
  kind: FieldKind;
  label: string;
  description?: string;
  defaultValue?: string;
  min?: number;
  max?: number;
}

export interface ContractManifest {
  name: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  category: "token" | "vesting" | "investor" | "aa" | "governance";
  fields: ContractField[];
}

export const deployApi = {
  available: () => api.get<AvailableContracts>("/admin/deploy/available"),
  manifests: () => api.get<{ manifests: ContractManifest[] }>("/admin/deploy/manifests"),
  manifest: (name: string) => api.get<ContractManifest>(`/admin/deploy/manifest/${name}`),
  list: () => api.get<{ deployments: Deployment[] }>("/admin/deploy/list"),
  latest: (contract: string) => api.get<Deployment>(`/admin/deploy/latest/${contract}`),
  deploy: (req: DeployRequest) => api.post<Deployment>("/admin/deploy", req),
};

// ─── Projects (bootstrap wizard) ─────────────────────────────────

export interface ProjectBeneficiary {
  label: string;
  wallet: string;
  allocationWei: string;
}

export interface ProjectSpec {
  slug: string;
  token: {
    name: string;
    symbol: string;
    totalSupplyWei: string;
  };
  vesting: {
    startUnix?: number;
    cliffSeconds: number;
    durationSeconds: number;
    manager: string;
    treasury: string;
  };
  beneficiaries: ProjectBeneficiary[];
}

export interface BootstrapStep {
  label: string;
  category: "token" | "vesting" | "investor" | "governance" | "aa" | "util";
  status: "success" | "failed" | "skipped";
  address?: string;
  txHash?: string;
  error?: string;
  reason?: string;
}

export interface ProjectInvite {
  email: string;
  label: string;
  code: string;
}

export interface ProjectBootstrapResult {
  ok?: boolean;
  error?: string;
  projectId?: number;
  slug?: string;
  tokenAddress?: string;
  vestingFactoryAddress?: string;
  vestings?: { wallet: string; vestingAddress: string; allocationWei: string }[];
  txHashes?: string[];
  steps?: BootstrapStep[];
  invites?: ProjectInvite[];
}

export interface ProjectListItem {
  id: number;
  slug: string;
  tokenName: string;
  tokenSymbol: string;
  totalSupply: string;
  tokenAddress: string | null;
  vestingFactoryAddress: string | null;
  status: "pending" | "completed" | "failed";
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export const projectsApi = {
  list: () => api.get<{ projects: ProjectListItem[] }>("/admin/projects"),
  bootstrap: (spec: ProjectSpec) => api.post<ProjectBootstrapResult>("/admin/projects/bootstrap", spec),
  resume: (slug: string) => api.post<ProjectBootstrapResult>(`/admin/projects/${slug}/resume`, {}),
  activity: (slug?: string) =>
    api.get<{ events: OnchainEvent[] }>(`/admin/projects/activity${slug ? `?slug=${slug}` : ""}`),
};
